import { Container, Paper, Grid, Typography, Box, Button, Alert, AlertTitle, CircularProgress } from "@mui/material";
import { useHistory } from "react-router-dom";
import React, { useEffect, useState } from "react";
import SendQuestionnaireData from "../ContactingAPIs/contactingCher.js";
import {
    CHUIAppBar,
    useCHUIAbrLanguageContext,
    useCHUIOldAbrLanguageContext,
    useCHUIAbrRegionContext,
    useCHUILanguageSupportContext,
    useCHUILanguageContext,
} from "@connecthealth-ui/core";
import { translateText } from "../ContactingAPIs/contactingOChad.js";
import { getJsonQuestionnaire } from "../questions/GetQuestionnaire.js";
import Element from "../Components/element";
import { FormikStepper, FormikStep } from "./FormikStepper.tsx";
import * as Validation from "../questions/validation";
import clinicallyTranslatedQuestionnaires from "../Utils/clinicallyTranslatedQuestionnaires.json";

const container = {
    padding: "0 0 20px 0",
    marginTop: "40px",
    justifyContent: "center",
};
const subContainer = {
    padding: "0 10px",
    paddingTop: "8px",
};
const question = {
    marginTop: "8px",
};

const clinicalTranslateBox = {
    display: "flex",
    justifyContent: "center",
};

const clinicalTranslateWarning = {
    color: "#cc0000",
    textAlign: "center",
};

export default function Questionnaire(props) {
    let params = new URL(document.location).searchParams;
    const history = useHistory();
    const UUID = params.get("UUID");
    const answersUuid = params.get("a");
    const questionnaireUUID = params.get("questionnaire");
    const patientPortalUuid = params.get("u");

    //this formInfo is what will be used to update the questionnaire, this should be updated when translating.
    const [formInfo, setFormInfo] = useState(null);
    const [formikStepperText, setFormikStepperText] = useState();
    const [formText, setFormText] = useState();
    const [formInfoPages, setFormInfoPages] = useState(null);
    const [yupValidation, setYupValidation] = useState(null);
    const [submitSuccess, setSubmitSuccess] = useState(null);
    const [submitSuccessMessage, setSubmitSuccessMessage] = useState("error");
    const [questionnaireDetails, setQuestionnaireDetails] = useState(null);
    const [standardJsonQuestions, setStandardJsonQuestions] = useState(null);
    const [initialValues, setInitialValues] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [retryingSubmission, setRetryingSubmission] = useState(false);

    //contexts
    const { selectedAbrRegion, setSelectedAbrRegion } = useCHUIAbrRegionContext();
    const { languageSupport, setLanguageSupport } = useCHUILanguageSupportContext();
    const { selectedLanguage, setSelectedLanguage } = useCHUILanguageContext();
    const { selectedAbrLanguage, setSelectedAbrLanguage } = useCHUIAbrLanguageContext("en");
    const { selectedOldAbrLanguage, setSelectedOldAbrLanguage } = useCHUIOldAbrLanguageContext();

    //an english standard of the form and text so that all translation is from the original language and text

    const standardValidationMessages = Validation.getValidationMessages();
    const standardFormikStepperText = {
        next: "next",
        submit: "submit",
        submitting: "submitting",
        errorMessage: "Invalid inputs, please check your answers",
        successfulSubmitMessage: "Form was successfully submitted!",
    };
    const standardFormText = {
        success: "Success",
        successMessage: "Your form was successfully submitted! You will be redirected in 3 seconds.",
        error: "Error",
        expire: "Questionnaire has expired",
        errorMessage: "Your answers did not submit successfully! Please check over your answers.",
        expireMessage: "Your time to complete this questionnaire has expired.",
        errorContinues:
            "Our system's are currently experiencing some issues please try again later.",
        expireContinues: "Please go back to your Questionnaire Portal, to view your remaining questionnaires.",
        retrying: "Retrying submission...",
        retryingMessage: "Your submission is taking longer than expected, please wait while we try again.",
        linkProvided: "Please use the provided link",
        clinicallyTranslatedDisclaimer:
            "Please be aware that the accuracy and appropriateness of this translation has not been checked, so some words may be incorrectly translated",
    };

    const initialDisclaimerArray = formInfo ? formInfo["initialDisclaimer"].split("[NEWLINE]") : null;
    const finalDisclaimerArray = formInfo ? formInfo["finalDisclaimer"].split("[NEWLINE]") : null;

    useEffect(() => {
        const language = params.get("l");
        const abrLanguage = params.get("al");
        const abrRegion = params.get("ar");
        if (language == null || abrLanguage == null || abrRegion == null) {
            return;
        }

        const support = true;

        if (abrLanguage != selectedAbrLanguage && abrLanguage != null) {
            setSelectedOldAbrLanguage(selectedAbrLanguage);
            setSelectedAbrLanguage(abrLanguage);
            setSelectedLanguage(language);
        }

        setSelectedAbrRegion(abrRegion != null ? abrRegion : selectedAbrRegion);
        setLanguageSupport(support != null ? support : languageSupport);

        if (questionnaireUUID != null) {
            setFormInfo(standardJsonQuestions);

            let pages = getFormPages(standardJsonQuestions);
            setFormInfoPages(pages);

            setFormikStepperText(standardFormikStepperText);

            setYupValidation(Validation.getValidation(questionnaireUUID, standardValidationMessages));
        }
    }, []);

    useEffect(async () => {
        setIsLoading(true);
        if (questionnaireUUID != null) {
            const localQuestionnaireDetails = await getJsonQuestionnaire(questionnaireUUID, answersUuid, UUID);
            setQuestionnaireDetails(localQuestionnaireDetails);

            const localStandardJsonQuestions = localQuestionnaireDetails ? localQuestionnaireDetails[0][0] : null;
            setStandardJsonQuestions(localStandardJsonQuestions);

            const localInitialValues = localQuestionnaireDetails ? localQuestionnaireDetails[1] : null;
            setInitialValues(localInitialValues);

            let localFormInfo = localStandardJsonQuestions;
            setFormInfo(localFormInfo);

            let pages = getFormPages(localFormInfo);
            setFormInfoPages(pages);

            setFormikStepperText(standardFormikStepperText);

            setYupValidation(Validation.getValidation(questionnaireUUID, standardValidationMessages));
        }

        setFormText(standardFormText);
        setIsLoading(false);
    }, []);

    useEffect(async () => {
        //translate questions and options
        if (selectedAbrLanguage === selectedOldAbrLanguage) return;

        if (selectedAbrLanguage !== "en") {
            //translate form text
            let translatedFormText = await translateText(
                JSON.stringify(standardFormText),
                selectedAbrLanguage,
                selectedOldAbrLanguage,
                ""
            );

            console.log(translatedFormText);

            if (questionnaireUUID != null) {
                let translatedFormInfo = await translateText(
                    JSON.stringify(standardJsonQuestions),
                    selectedAbrLanguage,
                    selectedOldAbrLanguage,
                    "formName, UUID, id, page, answerType, name, value, type, sliderRange, customChoiceOption, increment, followUpOption, followUpOptions, marks, row, followUpOptionId0, followUpOptionId1, selectColours, defaultColour, mediumColour, highColour, support, listHeaderId, requiredQuestions, rowManagerButtons, requiredQuestions, readOnly",
                    questionnaireUUID
                );

                //translate formikStatic text
                let translatedFormikText = await translateText(
                    JSON.stringify(standardFormikStepperText),
                    selectedAbrLanguage,
                    selectedOldAbrLanguage,
                    ""
                );

                let translatedValidationMessages = await translateText(
                    JSON.stringify(standardValidationMessages),
                    selectedAbrLanguage,
                    selectedOldAbrLanguage,
                    ""
                );

                //set values at bottom so there is no delay between each translation
                setYupValidation(Validation.getValidation(questionnaireUUID, translatedValidationMessages));
                setFormikStepperText(translatedFormikText);
                setFormInfo(translatedFormInfo);
                let pages = getFormPages(translatedFormInfo);
                setFormInfoPages(pages);
            }

            setFormText(translatedFormText);
        }

        setFormText(standardFormText);
    }, [selectedAbrLanguage]);

    return (
        <Paper square sx={{ minHeight: "100vh", padding: "0 0 30px 0", zIndex: 0 }}>
            {isLoading && (
                <Box
                    sx={{
                        display: "flex",
                        zIndex: 2,
                        position: "absolute",
                        height: "100%",
                        width: "100%",
                        justifyContent: "center",
                        alignItems: "center",
                        backgroundColor: "rgba(0,129, 201, 0.1)",
                    }}
                >
                    <CircularProgress />
                </Box>
            )}
            <Container sx={container}>
                {!isLoading && (
                    <Paper elevation={3}>
                        <Container sx={subContainer} maxWidth="md">
                            {UUID != null && questionnaireUUID != null && answersUuid != null && (
                                <Grid direction="row" justifyContent="center" alignItems="center">
                                    {submitSuccess !== 200 && (
                                        <>
                                            {!checkIfClinicallyTranslated(selectedAbrLanguage, questionnaireUUID) && (
                                                <Box sx={clinicalTranslateBox}>
                                                    <Typography sx={clinicalTranslateWarning} variant="h5">
                                                        {formText?.clinicallyTranslatedDisclaimer}
                                                    </Typography>
                                                </Box>
                                            )}

                                            <Typography>
                                                <Typography>
                                                    {initialDisclaimerArray
                                                        ? initialDisclaimerArray.map((element) => (
                                                            <p tabindex="0"> {element} </p>
                                                        ))
                                                        : null}
                                                </Typography>

                                                {initialValues != null && (
                                                    <FormikStepper
                                                        initialValues={initialValues}
                                                        onSubmit={async (values, { setSubmitting }) => {
                                                            let submitResult = await SendQuestionnaireData(
                                                                values,
                                                                questionnaireUUID,
                                                                UUID,
                                                                answersUuid,
                                                                questionnaireDetails,
                                                                setRetryingSubmission
                                                            );

                                                            if (
                                                                submitResult["StatusCode"] != null &&
                                                                submitResult["StatusCode"] !== 200
                                                            ) {
                                                                switch (submitResult["statusMessage"]) {
                                                                    case " Questionnaire has expired":
                                                                        setSubmitSuccessMessage("expired");
                                                                        break;
                                                                    default:
                                                                        setSubmitSuccessMessage("error");
                                                                        break;
                                                                }
                                                            }

                                                            setSubmitSuccess(submitResult["StatusCode"]);
                                                            window.scrollTo(0, 0);

                                                            setTimeout(() => {
                                                                history.push(
                                                                    `/questionnaire/QuestionnairePortal?UUID=${patientPortalUuid}`
                                                                );
                                                            }, 3700);

                                                            setSubmitting(false);
                                                        }}
                                                    >
                                                        {formInfoPages
                                                            ? formInfoPages.map((field, i) => {
                                                                return (
                                                                    <FormikStep
                                                                        label=""
                                                                        validationSchema={yupValidation[i]}
                                                                        valdidate
                                                                    >
                                                                        {field.map((field, i) => (
                                                                            <Typography
                                                                                sx={question}
                                                                                style={{ marginTop: "80px" }}
                                                                            >
                                                                                <Element
                                                                                    key={i}
                                                                                    questions={field}
                                                                                    sx={question}
                                                                                />
                                                                            </Typography>
                                                                        ))}
                                                                        {retryingSubmission && (
                                                                            <Grid
                                                                                container
                                                                                direction="row"
                                                                                justifyContent="center"
                                                                                alignItems="center"
                                                                                style={{
                                                                                    marginBottom: "20px",
                                                                                    marginTop: "30px",
                                                                                }}
                                                                            >
                                                                                <Alert severity="warning">
                                                                                    <AlertTitle>
                                                                                        {formText["retrying"]}
                                                                                    </AlertTitle>
                                                                                    <p>
                                                                                        {formText["retryingMessage"]}
                                                                                    </p>
                                                                                </Alert>
                                                                            </Grid>
                                                                        )}

                                                                        {submitSuccess != null &&
                                                                            submitSuccess !== 200 &&
                                                                            (submitSuccessMessage == "expired" ? (
                                                                                <Grid
                                                                                    container
                                                                                    direction="row"
                                                                                    justifyContent="center"
                                                                                    alignItems="center"
                                                                                    style={{
                                                                                        marginBottom: "20px",
                                                                                        marginTop: "30px",
                                                                                    }}
                                                                                >
                                                                                    <Alert severity="error">
                                                                                        <AlertTitle>
                                                                                            {formText["expire"]}
                                                                                        </AlertTitle>
                                                                                        <p>
                                                                                            {
                                                                                                formText[
                                                                                                "expireMessage"
                                                                                                ]
                                                                                            }
                                                                                        </p>
                                                                                        <p>
                                                                                            {
                                                                                                formText[
                                                                                                "expireContinues"
                                                                                                ]
                                                                                            }
                                                                                        </p>
                                                                                    </Alert>
                                                                                </Grid>
                                                                            ) : (
                                                                                <Grid
                                                                                    container
                                                                                    direction="row"
                                                                                    justifyContent="center"
                                                                                    alignItems="center"
                                                                                    style={{
                                                                                        marginBottom: "20px",
                                                                                        marginTop: "30px",
                                                                                    }}
                                                                                >
                                                                                    <Alert severity="error">
                                                                                        <AlertTitle>
                                                                                            {formText["error"]}
                                                                                        </AlertTitle>
                                                                                        <p>
                                                                                            {formText["errorMessage"]}
                                                                                        </p>
                                                                                        <p>
                                                                                            {
                                                                                                formText[
                                                                                                "errorContinues"
                                                                                                ]
                                                                                            }
                                                                                        </p>
                                                                                        <strong>
                                                                                            {window.location.href +
                                                                                                " failed with a response status " +
                                                                                                submitSuccess}
                                                                                        </strong>
                                                                                    </Alert>
                                                                                </Grid>
                                                                            ))}
                                                                    </FormikStep>
                                                                );
                                                            })
                                                            : null}
                                                        <FormikStep label={formikStepperText}></FormikStep>
                                                    </FormikStepper>
                                                )}
                                            </Typography>
                                        </>
                                    )}

                                    {submitSuccess === 200 && (
                                        <Grid
                                            container
                                            direction="row"
                                            justifyContent="center"
                                            alignItems="center"
                                            style={{ marginBottom: "20px" }}
                                        >
                                            <Alert severity="success">
                                                <AlertTitle>{formText["success"]}</AlertTitle>
                                                <strong>{formText["successMessage"]}</strong>
                                            </Alert>
                                        </Grid>
                                    )}

                                    <Typography style={{ marginTop: "60px" }}>
                                        {finalDisclaimerArray
                                            ? finalDisclaimerArray.map((element) => <p tabindex="0"> {element} </p>)
                                            : null}
                                    </Typography>
                                </Grid>
                            )}

                            {UUID == null && (
                                <Grid container direction="row" justifyContent="center" alignItems="center">
                                    <h2>{formText ? formText["linkProvided"] : null}</h2>
                                </Grid>
                            )}
                        </Container>
                    </Paper>
                )}
            </Container>
        </Paper>
    );
}

function getFormPages(formInfo) {
    let pages = [];
    let page = [];

    let questions = formInfo ? formInfo.questions : {};
    if (questions && Object.keys(questions).length === 0 && Object.getPrototypeOf(questions) === Object.prototype) {
        return pages;
    }
    let prevNumber = 1;
    questions.forEach((element) => {
        if (element["page"] !== prevNumber) {
            pages.push(page);
            page = [];
            prevNumber++;
        }

        page.push(element);
    });

    // push last page
    pages.push(page);

    return pages;
}

function checkIfClinicallyTranslated(languageAbr, questionnaireUuid) {
    if (!clinicallyTranslatedQuestionnaires[languageAbr]) return false;
    if (clinicallyTranslatedQuestionnaires[languageAbr] == "*") return true;

    if (clinicallyTranslatedQuestionnaires[languageAbr].includes(questionnaireUuid)) return true;

    return false;
}
